<template>
    <div>
        <div class="meal-status-inline-layout">
                <div class="meal-status-inline-checkboxes">
                    <fe-stepper-checkbox-panel
                        v-model="panelValues.mealStatuses"
                        :items="mealStatusItems"
                        width="100%"
                    />
                </div>
                <div class="meal-status-inline-options">
                    <fe-remote-combo
                        label="School Year"
                        v-model="panelValues.school_year"
                        rootProperty="years"
                        url="qry.php?action=get&property=school_years"
                        :error="schoolYearRequiredError"
                        :error-messages="schoolYearRequiredError ? 'School Year is required.' : ''"
                        data-test="meal-status-school-year-filter-field"
                    />
                    <v-switch v-model="panelValues.restrict" label="Restrict Results" hide-details :ripple="false" data-test="meal-status-restrict-results-switch"/>
                </div>
            </div>
    </div>
</template>

<script>
import * as Util from '../../Util'
import FilterUtil from '../../FilterPanelUtil'

export default {
    name: 'MealStatusPanel',
    rank: 1,

    security: ['SEARCH_MEAL_STATUS', 'DRILLDOWN_MEAL_STATUS'],

    props: {
        existingFilter: { type: Object, default: null },
    },

    data() {
        return {
            displayName: 'Meal Status',
            type: 'meal_status',
            mealStatusItems: [],
            panelValues: {
                mealStatuses: [],
                school_year: null,
                restrict: true,
            },
        }
    },

    computed: {
        schoolYearRequiredError() {
            const mealStatuses = this.panelValues.mealStatuses
            const schoolYear = this.panelValues.school_year
            return mealStatuses.length > 0 && !schoolYear
        },
        inlineValid() {
            return !this.$_.isEmpty(this.panelValues.mealStatuses) && !!this.panelValues.school_year
        },
        isDirty() {
            // Monitor form state to enable/disable apply button appropriately
            const applied = this.existingFilter?.editorParams
            if (!applied) return false
            const current = this.panelValues

            if (applied.restrict !== current.restrict) return true

            const appliedSchoolYearId = applied.school_year?.id ?? null
            const currentSchoolYearId = current.school_year?.id ?? null
            if (appliedSchoolYearId !== currentSchoolYearId) return true

            const appliedIds = !Array.isArray(applied.mealStatuses) ? [] : applied.mealStatuses.map(x => x?.id ?? x).sort((a, b) => a - b)
            const currentIds = !Array.isArray(current.mealStatuses) ? [] : current.mealStatuses.map(x => x?.id ?? x).sort((a, b) => a - b)
            return appliedIds.length !== currentIds.length || appliedIds.some((id, i) => id !== currentIds[i])
        },
    },

    watch: {
        existingFilter: {
            handler(f) {
                // Sync form state from the applied filter when it changes; clear form when the filter is removed.
                if (f?.editorParams) {
                    const ep = f.editorParams
                    this.panelValues.restrict = ep.restrict
                    this.panelValues.mealStatuses = ep.mealStatuses ? [...ep.mealStatuses] : []
                    this.panelValues.school_year = ep.school_year ? { ...ep.school_year } : null
                } else {
                    this.panelValues.mealStatuses = []
                    this.panelValues.school_year = null
                    this.panelValues.restrict = true
                    this.loadDefaultSchoolYear()
                }
            },
            immediate: true,
        },
        inlineValid: {
            handler(v) {
                this.$emit('valid-change', v)
            },
            immediate: true,
        },
        schoolYearRequiredError: {
            handler(v) {
                this.$emit('invalid-change', v)
            },
            immediate: true,
        },
        isDirty: {
            handler(v) {
                this.$emit('dirty-change', v)
            },
            immediate: true,
        },
    },

    mounted() {
        this.loadMealStatuses()
        this.loadDefaultSchoolYear()
        this.$emit('valid-change', this.inlineValid)
    },

    methods: {
        getFilter() {
            if (this.$_.isEmpty(this.panelValues.mealStatuses)) return null
            const name = this.panelValues.mealStatuses.map(x => x.display_name || x.name).join(', ')
            const ids = FilterUtil.collectIdsFromSelection(this.panelValues.mealStatuses, { idKey: 'id', parseInteger: true })
            const filter = {
                type: this.type,
                id: Util.UUIDv4(),
                restrict: this.panelValues.restrict,
                report: true,
                params: this.$_.pickBy({
                    meal_status_id: ids,
                    school_year_id: this.panelValues.school_year?.id,
                }, x => !this.$_.isBlank(x)),
            }
            FilterUtil.attachFilterDescriptor(filter, {
                editor: this,
                editorParams: {
                    restrict: this.panelValues.restrict,
                    mealStatuses: this.panelValues.mealStatuses ? [...this.panelValues.mealStatuses] : null,
                    school_year: this.panelValues.school_year ? { ...this.panelValues.school_year } : null,
                },
                chipText: name,
                tooltipData: this.$_.pickBy({
                    'Meal Status': name,
                    'Restrict': this.panelValues.restrict,
                    'School Year': this.panelValues.school_year?.name,
                }, x => !this.$_.isBlank(x)),
            })
            return filter
        },

        loadDefaultSchoolYear() {
            if (this.existingFilter?.editorParams?.school_year) return
            this.$modelGet('schoolYear').then((r) => {
                const years = Array.isArray(r) ? r : (r?.years ?? [])
                if (years?.length && !this.panelValues.school_year) {
                    this.$set(this.panelValues, 'school_year', years[0])
                }
            })
        },
        loadMealStatuses() {
            this.$modelGet('mealStatusType')
                .then((res) => {
                    // $modelGet resolves with the rootProperty value (the array), not response.data
                    const data = Array.isArray(res) ? res : (res?.data?.meal_status ?? res?.meal_status ?? [])
                    const filtered = []
                    data.forEach((itm) => {
                        if (!itm.display_name_group) {
                            itm.name = itm.display_name || itm.name
                            filtered.push(itm)
                        } else {
                            const o = data.find(x => x.id == itm.display_name_group[0]) // id can be string
                            if (!filtered.find(x => x.display_name === o.display_name)) {
                                filtered.push({ ...o, name: o.display_name || o.name })
                            }
                        }
                    })
                    this.mealStatusItems = filtered
                })
        },
    }
}
</script>

<style lang="scss" scoped>
.meal-status-inline-layout {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 0 24px;
    width: 100%;
    min-width: 0;
}

.meal-status-inline-checkboxes {
    flex: 1 1 0;
    min-width: 0;
}

.meal-status-inline-options {
    display: flex;
    flex-direction: column;
    gap: 12px 0;
    flex: 1 1 0;
    min-width: 0;
}

</style>
